/**
 * ReadingCoursesHub.jsx — Reader Training Courses hub.
 *
 * 10 modules across 4 tiers (Foundation / Developing / Advanced /
 * Mastery), with level-gated unlocks at Reader Level 1 / 5 / 15 / 25.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, CheckCircle2, Lock, ChevronRight, Loader2, Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { authFetch } from '../utils/authUtils';
import ReaderStatsCard from '../components/ReaderStatsCard';
import TrialGateBanner from '../components/TrialGateBanner';
import useTrialGate from '../hooks/useTrialGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReadingCoursesHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(null);
  // Modules gate — peek only so browsing the catalog doesn't start the
  // 24h clock. Clock starts on lesson open (`/api/reader/courses/lesson/{id}`).
  const { status: trialStatus, expired: trialExpired } =
    useTrialGate('modules', { peek: true });

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/reader/courses/catalog`);
        if (r.ok) setCat(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading courses…
      </div>
    );
  }
  if (!cat) return null;

  const byTier = {};
  for (const m of cat.modules) {
    byTier[m.tierId] = byTier[m.tierId] || [];
    byTier[m.tierId].push(m);
  }
  for (const t in byTier) byTier[t].sort((a, b) => a.order - b.order);

  const readerLevel = cat.viewer?.readerLevel || 1;

  return (
    <main className="max-w-5xl mx-auto px-4 py-8" data-testid="reading-courses-hub">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-indigo-400" /> Reading Courses
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Ten modules across four tiers. Every pass earns Reader XP, shop coins, and lifts your core stats.
        </p>
      </div>

      <div className="mb-6">
        <ReaderStatsCard />
      </div>

      {/* Feb 14 2026 — 1-day free-trial gate for Modules. */}
      <TrialGateBanner status={trialStatus} feature="modules" />

      <div className="grid grid-cols-1 gap-6">
        {cat.tiers.map((t) => {
          const modules = byTier[t.id] || [];
          const prog = cat.progress?.[t.id] || { completed: 0, total: modules.length, pct: 0, unlocked: false };
          const tierLocked = !prog.unlocked;
          return (
            <Card
              key={t.id}
              className="bg-slate-800/50 border-slate-700 overflow-hidden"
              data-testid={`tier-${t.id}`}
            >
              <div
                className="p-4 flex items-center gap-3 border-b border-slate-700/60"
                style={{ background: `linear-gradient(90deg, ${t.colour}22, transparent)` }}
              >
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${t.colour}22`, border: `1px solid ${t.colour}66` }}
                >
                  {tierLocked ? (
                    <Lock className="w-5 h-5" style={{ color: t.colour }} />
                  ) : (
                    <Sparkles className="w-5 h-5" style={{ color: t.colour }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-white">{t.name} Tier</h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${t.colour}22`, color: t.colour, border: `1px solid ${t.colour}55` }}>
                      Unlocks at L{t.unlockLevel}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{t.description}</p>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="text-xs font-bold"
                    style={{ color: t.colour }}
                    data-testid={`tier-progress-${t.id}`}
                  >
                    {prog.completed}/{prog.total} · {prog.pct}%
                  </div>
                  <div className="w-24 h-1.5 bg-slate-900/60 rounded-full overflow-hidden mt-1">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${prog.pct}%`, backgroundColor: t.colour }}
                    />
                  </div>
                </div>
              </div>

              <CardContent className="p-3">
                {tierLocked && (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-xs text-gray-300">
                    Reach <span className="font-bold text-white">Reader Level {t.unlockLevel}</span> to unlock this tier. You're currently Level {readerLevel}.
                  </div>
                )}
                <ul className="space-y-2">
                  {modules.map((m) => {
                    const locked = !!m.locked;
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          disabled={locked}
                          onClick={() => {
                            if (trialExpired) {
                              navigate('/premium');
                              return;
                            }
                            navigate(`/reading-course/lesson/${m.id}`);
                          }}
                          data-testid={`lesson-row-${m.id}`}
                          className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            locked
                              ? 'bg-slate-900/40 border-slate-800 opacity-50 cursor-not-allowed'
                              : trialExpired
                              ? 'bg-slate-900/40 border-amber-500/30 hover:border-amber-400/60'
                              : m.completed
                              ? 'bg-emerald-500/5 border-emerald-500/30 hover:bg-emerald-500/10'
                              : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <div
                            className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              m.completed
                                ? 'bg-emerald-500 text-white'
                                : locked
                                ? 'bg-slate-700 text-gray-500'
                                : 'bg-slate-700 text-gray-200'
                            }`}
                          >
                            {m.completed ? <CheckCircle2 className="w-4 h-4" /> : locked ? <Lock className="w-3.5 h-3.5" /> : m.order}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{m.title}</div>
                            <div className="text-[11px] text-gray-400 truncate">
                              {m.path} · {m.lessonCount} lessons · {m.quizCount}-question quiz · +{m.xp} XP · +{m.coins} coins
                            </div>
                          </div>
                          {!locked && <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </main>
  );
};

export default ReadingCoursesHub;
